import { Box, Tabs, TabList, Tab, TabPanel, ListItemDecorator, tabClasses } from "@mui/joy";
import { useResponsive } from "../../../../../../hooks";

interface ITabItem {
    name: string;
    icon?: any;
    component?: any;
}

interface INavigationProps {
    tabList: ITabItem[];
    defaultValue?: string | number;
}

export function Navigation({ tabList, defaultValue }: INavigationProps) {
    const initial = defaultValue ?? tabList?.[0]?.name ?? 0;
    const { isMobile, isTablet } = useResponsive();
    const isCompact = isMobile || isTablet; // < 900px

    return (
        <Box sx={{ width: "100%" }}>
            <Tabs
                defaultValue={initial}
                size="sm"
                orientation={isCompact ? "horizontal" : "vertical"}
                sx={{
                    width: "100%",
                    bgcolor: "transparent",
                    "& .MuiTabs-indicator": { display: "none" },
                }}
            >
                {/* ── Tab List ── */}
                <TabList
                    variant="soft"
                    sx={{
                        borderRadius: "8px",
                        p: 0.5,
                        gap: 0.5,
                        bgcolor: "background.level1",

                        // Vertical (desktop): fixed-width sidebar
                        ...(!isCompact && {
                            width: 200,
                            minWidth: 200,
                            flexShrink: 0,
                            height: "100%",
                            "& button": {
                                justifyContent: "flex-start",
                                minHeight: 32,
                                borderRadius: 6,
                            },
                            "& button[aria-selected='true']": {
                                fontWeight: 600,
                                borderRight: "3px solid",
                                "&::after": { width: 0 },
                            },
                        }),

                        // Horizontal (mobile/tablet): scrollable row
                        ...(isCompact && {
                            width: "100%",
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                            flexDirection: "row",
                            "& button": {
                                flex: 1,
                                minHeight: 36,
                                borderRadius: 6,
                                justifyContent: "center",
                                whiteSpace: "nowrap",
                                fontSize: "0.8rem",
                            },
                            [`& button.${tabClasses.selected}`]: {
                                fontWeight: 600,
                                "&::after": { display: "none" },
                            },
                        }),
                    }}
                >
                    {tabList?.map((t) => (
                        <Tab key={t.name} value={t.name}>
                            {t.icon && <ListItemDecorator>{t.icon}</ListItemDecorator>}
                            {t.name}
                        </Tab>
                    ))}
                </TabList>

                {/* ── Tab Panels ── */}
                {tabList?.map((t) => (
                    <TabPanel
                        key={t.name}
                        value={t.name}
                        sx={{
                            p: 0,
                            ml: isCompact ? 0 : 3,
                            mt: isCompact ? 2 : 0,
                            minWidth: 0,
                        }}
                    >
                        {t.component}
                    </TabPanel>
                ))}
            </Tabs>
        </Box>
    );
}
